import { IonIcon } from "@ionic/react";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { chevronBackOutline, chevronForwardOutline, flashOutline, ticketOutline } from "ionicons/icons";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import Info from "../../image/icons/map/details/Info.svg?react";
import { TicketPurchaseApi } from "../../services/TicketPurchaseApi";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ephemeralStore, ticketPurchaseSlice, useEphemeralSelector } from "../../store/ephemeral";
import type { SelectedPurchaseOffer } from "../../store/ephemeral/slices/ticketPurchase.slice";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { formatStopName } from "../../tools/stopNameFormatter";
import type { ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import type { PurchaseCheckoutModeDTO } from "../../types/dtos/api/purchase/purchase-payment.dto";
import type { CffSalesDateTime, CffSalesOffer, CffSalesTicketPeriod } from "../../types/dtos/api/sales/cff-sales-offer.dto";
import type { SwissCffProviderOffer } from "../../types/dtos/api/sales/provider-offer.dto";
import { CitySupplementBadge } from "../shared/CitySupplementBadge";
import { DIRECT_PAYMENT_BENEFIT_TEXT, DIRECT_PAYMENT_CONSENT_TEXT } from "./directPayment.constants";
import { OFFER_REFRESH_ERROR_CODES, readPurchaseError, refreshTicketPurchaseOffer } from "./ticketPurchaseOfferRefresh.tools";
import {
    buildSelectedOfferDtos,
    CFF_JOURNEY_TICKET_PRODUCT_ID,
    getCffCityAreaLabel,
    isCffCityTicket,
    remapSelectionToRefreshedOffers,
    resolveCffConversationId,
} from "./ticketPurchaseSegments.tools";

/**
 * Refreshes the itinerary offer after `OFFER_EXPIRED` / `OFFER_PRICE_CHANGED`.
 *
 * The previous selection is remapped onto the fresh offers positionally when
 * possible, landing the shopper back on the summary with a notice — re-doing
 * the selection only happens when the offer structure changed. The new
 * itinerary triggers the modal segment reload (attempt id cleared).
 *
 * @param itinerary - Itinerary whose offer must be refreshed.
 * @param previousSelection - Selection the shopper made before the refresh.
 */
async function refreshOffersAfterChange(
    itinerary: ItineraryDTO,
    previousSelection: Record<string, SelectedPurchaseOffer>,
): Promise<void> {
    ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
        ...prev,
        "error": "Mise à jour des offres…",
    } ) );

    try {
        const freshItinerary = await refreshTicketPurchaseOffer( itinerary );
        const remapped = remapSelectionToRefreshedOffers( freshItinerary, previousSelection );

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "itinerary":             freshItinerary,
            "step":                  remapped !== null ? "summary" : "selection",
            "selectionSegmentIndex": 0,
            "selectedOffers":        remapped ?? {},
            "prebookingResponse":    null,
            "purchaseAttemptId":     null,
            "paymentId":             null,
            "paymentStatus":         "idle",
            "error":                 null,
            "notice":                remapped !== null
                ? "Les offres ou leurs prix ont été mis à jour. Vérifiez le récapitulatif."
                : "Les offres ou leurs prix ont été mis à jour. Vérifiez votre sélection.",
        } ) );
    } catch {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "error": "Les offres ont expiré et n'ont pas pu être mises à jour. Fermez puis rouvrez l'achat depuis l'itinéraire.",
        } ) );
    }
}

const formatOfferDescription = ( offer: CffSalesOffer | undefined ): string => {
    if ( offer === undefined ) {
        return "Billet";
    }

    if ( offer.productId === CFF_JOURNEY_TICKET_PRODUCT_ID ) {
        return "Billet de parcours";
    }

    if ( isCffCityTicket( offer ) ) {
        const leading = getCffCityAreaLabel( offer.validityArea?.leadingArea );
        const tailing = getCffCityAreaLabel( offer.validityArea?.tailingArea );

        if ( leading !== null && tailing !== null ) {
            return `City ${ leading } · City ${ tailing }`;
        }

        return `City ${ leading ?? tailing ?? "" }`.trim();
    }

    const firstPart = offer.title.split( ":" )[0]?.trim() ?? offer.description ?? "Billet";

    if ( firstPart.length === 0 ) {
        return "Billet";
    }

    return `${ firstPart.charAt( 0 ).toUpperCase() }${ firstPart.slice( 1 ) }`;
};

interface TicketOrderSummaryProps { "isDarkUi": boolean }

/** One priced line of a summary leg — plain product line or City supplement badge. */
type OrderSummaryItem =
  | {
      "isCity":      false;
      "description": string;
  } |
  {
      "isCity":    true;
      "city":      string;
      "areaName"?: string;
  };

const formatTime = ( iso: string ): string => {
    try {
        return new Date( iso ).toLocaleTimeString( "fr-CH", {
            "hour":   "2-digit",
            "minute": "2-digit",
        } );
    } catch {
        return "--:--";
    }
};

const formatDateParts = ( iso: string ): {
    "weekday":  string;
    "dayMonth": string;
} => {
    try {
        const date = new Date( iso );

        return {
            "weekday":  date.toLocaleDateString( "fr-CH", { "weekday": "short" } ).replace( ".", "" ),
            "dayMonth": date.toLocaleDateString( "fr-CH", {
                "day":   "2-digit",
                "month": "2-digit",
            } ),
        };
    } catch {
        return {
            "weekday":  "",
            "dayMonth": "",
        };
    }
};

const formatDuration = ( fromIso: string, toIso: string ): string => {
    try {
        const diffMs = new Date( toIso ).getTime() - new Date( fromIso ).getTime();

        if ( !Number.isFinite( diffMs ) || diffMs <= 0 ) {
            return "";
        }

        const totalMinutes = Math.round( diffMs / 60000 );
        const hours = Math.floor( totalMinutes / 60 );
        const minutes = totalMinutes % 60;

        return hours > 0 ? `${ hours } h ${ String( minutes ).padStart( 2, "0" ) }` : `${ minutes } min`;
    } catch {
        return "";
    }
};

const parseCffDateTime = ( dateTime: CffSalesDateTime | undefined ): Date | null => {
    if ( dateTime === undefined ) {
        return null;
    }

    try {
        const date = new Date( `${ dateTime.date }T${ dateTime.time ?? "00:00:00" }` );

        if ( Number.isNaN( date.getTime() ) ) {
            return null;
        }

        return date;
    } catch {
        return null;
    }
};

const formatTicketValidity = ( validity: CffSalesTicketPeriod | undefined ): string | null => {
    if ( validity?.from === undefined || validity?.to === undefined ) {
        return null;
    }

    const from = parseCffDateTime( validity.from );
    const to = parseCffDateTime( validity.to );

    if ( from === null || to === null ) {
        return null;
    }

    const fromHasTime = validity.from.time !== undefined;
    const toHasTime = validity.to.time !== undefined;
    const isAllDay = !fromHasTime && !toHasTime;

    const isSameDate =
        from.getFullYear() === to.getFullYear() &&
        from.getMonth() === to.getMonth() &&
        from.getDate() === to.getDate();

    const currentYear = new Date().getFullYear();

    const formatValidityDate = ( date: Date ): string => ( date.getFullYear() === currentYear
        ? date.toLocaleDateString( "fr-CH", {
            "day":   "2-digit",
            "month": "2-digit",
        } )
        : date.toLocaleDateString( "fr-CH", {
            "day":   "2-digit",
            "month": "2-digit",
            "year":  "numeric",
        } ) );

    const formatValidityTime = ( date: Date ): string => date.toLocaleTimeString( "fr-CH", {
        "hour":   "2-digit",
        "minute": "2-digit",
    } );

    if ( isSameDate ) {
        if ( isAllDay ) {
            return `Valide le ${ formatValidityDate( from ) }`;
        }

        return `Valide de ${ formatValidityTime( from ) } à ${ formatValidityTime( to ) }`;
    }

    if ( isAllDay ) {
        return `Valide du ${ formatValidityDate( from ) } au ${ formatValidityDate( to ) }`;
    }

    return `Valide du ${ formatValidityDate( from ) } ${ formatValidityTime( from ) } au ${ formatValidityDate( to ) } ${ formatValidityTime( to ) }`;
};

/**
 * `mask-image` value fading the recap edge(s) that still hide content — the
 * scroll cue the ticket verso description uses, instead of a scrollbar.
 */
const summaryScrollMask = {
    "none":   undefined,
    "bottom": "linear-gradient(to bottom, black calc(100% - 2rem), transparent)",
    "top":    "linear-gradient(to bottom, transparent, black 2rem)",
    "both":   "linear-gradient(to bottom, transparent, black 2rem, black calc(100% - 2rem), transparent)",
} as const;

export const TicketOrderSummary: React.FC<TicketOrderSummaryProps> = ( { isDarkUi } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const state = useEphemeralSelector( ticketPurchaseSlice, ( s: TicketPurchaseState ) => s );

    const { itinerary, selectedOffers } = state;

    const legs = useMemo( () => {
        if ( itinerary?.offer === undefined ) {
            return [];
        }

        return Object.entries( itinerary.offer.segments ).flatMap( ( [ segmentId, segment ] ) => {
            const bundles = ( segment.providerOffers ?? [] ).filter( ( offer ): offer is SwissCffProviderOffer => offer.provider === "swiss_cff" );

            if ( bundles.length === 0 ) {
                return [];
            }

            const pricingSegment = itinerary.pricingSegments?.find( ( s ) => s.id === segmentId );
            const selection = selectedOffers[segmentId];
            const bundle = selection !== undefined ? bundles[selection.bundleIndex] : undefined;
            const offer = selection !== undefined ? bundle?.offers[selection.offerIndex] : undefined;

            const isCityOffer = offer !== undefined && isCffCityTicket( offer );
            const cityItems = isCityOffer
                ? [ offer.validityArea?.leadingArea, offer.validityArea?.tailingArea ].
                    map( ( area ) => ( {
                        "city":     getCffCityAreaLabel( area ),
                        "areaName": area?.areaName,
                    } ) ).
                    filter( ( item ): item is {
                        "city":     string;
                        "areaName": string | undefined;
                    } => item.city !== null )
                : [];

            return [
                {
                    "segmentId": segmentId,
                    "from":      formatStopName( pricingSegment?.from?.name ?? "" ),
                    "to":        formatStopName( pricingSegment?.to?.name ?? "" ),
                    "fromTime":  pricingSegment?.from?.time ?? "",
                    "toTime":    pricingSegment?.to?.time ?? "",
                    "skipped":   selection === undefined,
                    "price":     offer?.price ?? 0,
                    "class":     offer?.qualityOfService ?? "2",
                    "validity":  formatTicketValidity( offer?.ticketValidity ),
                    "items":     ( offer === undefined
                        ? []
                        : isCityOffer
                            ? [
                                {
                                    "description": "Billet de parcours",
                                    "isCity":      false,
                                },
                                ...cityItems.map( ( item ) => ( {
                                    ...item,
                                    "isCity": true,
                                } ) ),
                            ]
                            : [
                                {
                                    "description": formatOfferDescription( offer ),
                                    "isCity":      false,
                                },
                            ] ) as OrderSummaryItem[],
                },
            ];
        } );
    }, [ itinerary, selectedOffers ] );

    const lines = useMemo( () => legs.flatMap( ( leg ) => leg.items ), [ legs ] );

    const total = useMemo( () => legs.reduce( ( sum, leg ) => sum + leg.price, 0 ), [ legs ] );

    const ticketCount = useMemo( () => legs.filter( ( leg ) => !leg.skipped ).length, [ legs ] );

    const goBack = useCallback( () => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "step":                  "selection",
            "selectionSegmentIndex": 0,
        } ) );
    }, [] );

    const selectCheckoutMode = useCallback( ( mode: PurchaseCheckoutModeDTO ) => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "checkoutMode":        mode,
            "checkoutModeTouched": true,
        } ) );
    }, [] );

    // Fade the recap edge that still hides content — tracks scroll + size.
    const summaryRef = useRef<HTMLDivElement>( null );
    const [ scrollFade, setScrollFade ] = useState<keyof typeof summaryScrollMask>( "none" );
    const updateScrollFade = useCallback( () => {
        const element = summaryRef.current;

        if ( element === null || element.scrollHeight - element.clientHeight <= 2 ) {
            setScrollFade( "none" );

            return;
        }

        const up = element.scrollTop > 2;
        const down = element.scrollTop + element.clientHeight < element.scrollHeight - 2;

        setScrollFade( up && down ? "both" : up ? "top" : down ? "bottom" : "none" );
    }, [] );

    useLayoutEffect( () => {
        const element = summaryRef.current;

        if ( element === null ) {
            return;
        }

        // `observe` fires an initial async callback — no sync setState needed.
        if ( typeof ResizeObserver === "undefined" ) {
            const frame = requestAnimationFrame( updateScrollFade );

            return () => cancelAnimationFrame( frame );
        }

        const observer = new ResizeObserver( updateScrollFade );

        observer.observe( element );

        return () => observer.disconnect();
    }, [ legs, updateScrollFade ] );

    const confirm = useCallback( async( checkoutMode: PurchaseCheckoutModeDTO ) => {
        if ( itinerary === null ) {
            return;
        }

        const conversationId = resolveCffConversationId( itinerary );
        const selected = buildSelectedOfferDtos( itinerary, selectedOffers );

        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
            ...prev,
            "step":                "payment",
            checkoutMode,
            "checkoutModeTouched": true,
            "prebookingResponse":  null,
            "purchaseAttemptId":   null,
            "paymentId":           null,
            "payrexxUrl":          null,
            "paymentStatus":       "idle",
            "error":               null,
            "notice":              null,
        } ) );

        try {
            const prebookResponse = await TicketPurchaseApi.postPurchasePrebook( {
                "itineraryId":    itinerary.id,
                conversationId,
                itinerary,
                "selectedOffers": selected,
            } );

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "prebookingResponse": prebookResponse,
                "purchaseAttemptId":  prebookResponse.purchaseAttemptId,
                "error":              null,
            } ) );
        } catch( error ) {
            const { status, code } = readPurchaseError( error );

            /*
             * The server-side offer registry no longer knows the offer or its price changed:
             * refresh the offer (also propagated to the planner) and let the shopper re-select.
             * Each refresh needs a new confirmation, so this can never loop on its own.
             */
            if ( status === 409 && code !== null && OFFER_REFRESH_ERROR_CODES.has( code ) ) {
                await refreshOffersAfterChange( itinerary, selectedOffers );

                return;
            }

            let message = "La préparation de votre commande a échoué.";

            if ( axios.isAxiosError( error ) ) {
                const data = error.response?.data as { "message"?: string | string[] } | undefined;
                const serverMessage = Array.isArray( data?.message ) ? data?.message[0] : data?.message;

                message = serverMessage ?? error.message;
            } else if ( error instanceof Error ) {
                message = error.message;
            }

            ephemeralStore.setValue( ticketPurchaseSlice, ( prev ): TicketPurchaseState => ( {
                ...prev,
                "error": message,
            } ) );
        }
    }, [ itinerary, selectedOffers ] );

    const contentBackground = isDarkUi ? "rgba(0, 0, 0, 0.22)" : "rgba(15, 23, 42, 0.05)";
    const cardBackground = palette.modalCardBackground;

    return (
        <div className="flex h-full flex-col">
            <div
                className="flex flex-1 flex-col overflow-hidden p-[12px]"
                style={{ "backgroundColor": contentBackground }}
            >
                <div
                    className="relative flex min-h-0 flex-1 flex-col rounded-2xl border p-4"
                    style={{
                        "backgroundColor": cardBackground,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div className="mb-6 flex shrink-0 items-center gap-2">
                        <IonIcon
                            aria-hidden="true"
                            icon={ticketOutline}
                            style={{
 "color":    palette.brandPrimary,
"fontSize": "18px",
}}
                        />
                        <h3 className="text-sm font-bold tracking-tight">
                            Récapitulatif de votre commande
                        </h3>
                    </div>
                    <div
                        className="relative min-h-0 flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden"
                        onScroll={updateScrollFade}
                        ref={summaryRef}
                        style={{
                            "maskImage":       summaryScrollMask[scrollFade],
                            "scrollbarWidth":  "none",
                            "WebkitMaskImage": summaryScrollMask[scrollFade],
                        }}
                    >
                        <div
                            className="absolute bottom-0 left-[5px] top-2 border-l border-dashed"
                            style={{ "borderColor": palette.overlayBorderStrong }}
                        />
                        {legs.map( ( leg ) => {
                            const subtotal = leg.price;

                            return (
                                <div
                                    className="relative pb-4 pl-6 last:pb-0"
                                    key={leg.segmentId}
                                >
                                    <div
                                        className={`absolute left-0 top-[5px] h-[11px] w-[11px] rounded-full border-2 ${ leg.skipped ? "border-dashed" : "" }`}
                                        style={{
                                            "backgroundColor": cardBackground,
                                            "borderColor":     leg.skipped ? palette.overlayBorderStrong : palette.brandPrimary,
                                        }}
                                    />
                                    <div className={leg.skipped ? "opacity-50" : ""}>
                                        <div className="flex items-baseline justify-between gap-3">
                                            <span className="truncate text-sm font-bold tracking-tight">
{leg.from}
{" → "}
{leg.to}
                                            </span>
                                            {!leg.skipped && (
                                                <span
                                                    className="shrink-0 text-sm font-bold"
                                                    style={{ "color": palette.brandPrimary }}
                                                >
                                                    {subtotal.toFixed( 2 )}
{" "}
CHF
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1.5 flex items-center gap-2.5">
                                            <div
                                                className="flex w-11 shrink-0 flex-col items-center rounded-lg border py-1"
                                                style={{
                                                    "backgroundColor": palette.selectorIdleBackground,
                                                    "borderColor":     palette.overlayBorder,
                                                }}
                                            >
                                                <span
                                                    className="text-[8px] font-semibold uppercase leading-none tracking-wide"
                                                    style={{ "color": palette.overlayTextSecondary }}
                                                >
{formatDateParts( leg.fromTime ).weekday}
                                                </span>
                                                <span
                                                    className="mt-0.5 text-[11px] font-bold leading-tight"
                                                    style={{ "color": palette.modalCardTextPrimary }}
                                                >
{formatDateParts( leg.fromTime ).dayMonth}
                                                </span>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center justify-between gap-3">
                                                    <span
                                                        className="text-xs font-bold"
                                                        style={{ "color": palette.modalCardTextPrimary }}
                                                    >
{formatTime( leg.fromTime )}
{" → "}
{formatTime( leg.toTime )}
                                                    </span>
                                                    {!leg.skipped && (
                                                        <span
                                                            className="shrink-0 rounded-full border px-2 py-px text-[10px] font-semibold leading-tight"
                                                            style={{
                                                                "backgroundColor": palette.selectorIdleBackground,
                                                                "borderColor":     palette.overlayBorder,
                                                                "color":           palette.modalCardTextPrimary,
                                                            }}
                                                        >
                                                            {leg.class}
e classe
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    className="mt-0.5 text-[10px]"
                                                    style={{ "color": palette.overlayTextSecondary }}
                                                >
{formatDuration( leg.fromTime, leg.toTime )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-1.5 space-y-1">
                                            {leg.skipped
? (
                                                <span
                                                    className="text-xs italic"
                                                    style={{ "color": palette.overlayTextSecondary }}
                                                >
                                                    Aucun billet pour ce segment
                                                </span>
                                            )
: leg.items.map( ( item, index ) => (
                                                item.isCity
? (
                                                    <div
                                                        className="flex items-center"
                                                        key={index}
                                                    >
                                                        <CitySupplementBadge
                                                            areaName={item.areaName}
                                                            city={item.city}
                                                            isDarkUi={isDarkUi}
                                                        />
                                                    </div>
                                                )
: (
                                                    <div
                                                        className="flex items-center"
                                                        key={index}
                                                    >
                                                        <span
                                                            className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold"
                                                            style={{
                                                                "backgroundColor": cardBackground,
                                                                "borderColor":     palette.statusOnTimeText,
                                                                "color":           palette.statusOnTimeText,
                                                            }}
                                                        >
{item.description}
                                                        </span>
                                                    </div>
                                                )
                                            ) )}
                                        </div>
                                        {!leg.skipped && leg.validity !== null && (
                                            <div
                                                className="mt-2 text-[10px]"
                                                style={{ "color": palette.overlayTextSecondary }}
                                            >
                                                {leg.validity}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        } )}
                    </div>
                    <div className="relative -mx-4 mt-3 shrink-0">
                        <div
                            className="border-t border-dashed"
                            style={{ "borderColor": palette.overlayBorderStrong }}
                        />
                    </div>
                    <div className="mt-3 shrink-0">
                        <div className="flex items-center justify-between gap-2">
                            <span
                                className="text-[10px] font-semibold uppercase tracking-widest"
                                style={{ "color": palette.overlayTextSecondary }}
                            >
                                Mode de paiement
                            </span>
                            {/* The ⓘ control sits beside the selector, never inside an option button. */}
                            <Tooltip
                                arrow
                                enterTouchDelay={0}
                                leaveTouchDelay={7500}
                                title={(
                                    <>
                                        <span className="mb-1 block font-semibold">
                                            {DIRECT_PAYMENT_BENEFIT_TEXT}
                                        </span>
                                        <span className="block">
                                            {DIRECT_PAYMENT_CONSENT_TEXT}
                                        </span>
                                    </>
                                )}
                            >
                                <span
                                    aria-label="Informations sur le paiement en un clic"
                                    className="inline-flex shrink-0 items-center justify-center"
                                    role="button"
                                    tabIndex={0}
                                >
                                    <Info
                                        className="h-4 w-4"
                                        style={{ "color": palette.overlayTextSecondary }}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                        <div
                            className="mt-1.5 flex gap-1 rounded-xl border p-1"
                            style={{ "borderColor": palette.overlayBorderStrong }}
                        >
                            <button
                                aria-pressed={state.checkoutMode === "standard"}
                                className="flex flex-1 items-center justify-center rounded-lg px-2 py-1.5 text-xs font-bold transition-opacity hover:opacity-80"
                                onClick={() => selectCheckoutMode( "standard" )}
                                style={state.checkoutMode === "standard"
                                    ? {
                                        "backgroundColor": `color-mix(in srgb, ${ palette.brandPrimary } 12%, transparent)`,
                                        "color":           palette.brandPrimary,
                                    }
                                    : { "color": palette.overlayTextSecondary }}
                                type="button"
                            >
                                Standard
                            </button>
                            <button
                                aria-pressed={state.checkoutMode === "direct"}
                                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold transition-opacity hover:opacity-80"
                                onClick={() => selectCheckoutMode( "direct" )}
                                style={state.checkoutMode === "direct"
                                    ? {
                                        "backgroundColor": `color-mix(in srgb, ${ palette.brandPrimary } 12%, transparent)`,
                                        "color":           palette.brandPrimary,
                                    }
                                    : { "color": palette.overlayTextSecondary }}
                                type="button"
                            >
                                <IonIcon
                                    aria-hidden="true"
                                    icon={flashOutline}
                                    style={{ "fontSize": "14px" }}
                                />
                                En un clic
                            </button>
                        </div>
                    </div>
                    <div
                        className="-mx-4 -mb-4 mt-3 flex shrink-0 items-end justify-between rounded-b-2xl px-4 py-3"
                        style={{ "backgroundColor": `color-mix(in srgb, ${ palette.brandPrimary } 10%, transparent)` }}
                    >
                        <div>
                            <span
                                className="block text-[10px] font-semibold uppercase tracking-widest"
                                style={{ "color": palette.statusOnTimeText }}
                            >
Total à payer
                            </span>
                            <span
                                className="mt-0.5 block text-[10px]"
                                style={{
 "color":   palette.statusOnTimeText,
"opacity": 0.75,
}}
                            >
                                {ticketCount}
{" "}
{ticketCount > 1 ? "billets" : "billet"}
                            </span>
                        </div>
                        <div className="flex items-baseline gap-0.5">
                            <span
                                className="text-[10px] font-bold uppercase tracking-wide"
                                style={{ "color": palette.statusOnTimeText }}
                            >
CHF
                            </span>
                            <span
                                className="text-2xl font-black leading-none tracking-tight"
                                style={{ "color": palette.statusOnTimeText }}
                            >
                                {total.toFixed( 2 ).split( "." )[0]}
                            </span>
                            <span
                                className="text-sm font-bold leading-none"
                                style={{ "color": palette.statusOnTimeText }}
                            >
                                .
{total.toFixed( 2 ).split( "." )[1]}
                            </span>
                        </div>
                    </div>
                </div>
                {state.notice !== null && (
                    <p
                        className="mt-4 rounded-xl border px-3 py-2 text-center text-xs"
                        role="status"
                        style={{
                            "borderColor": palette.overlayBorder,
                            "color":       palette.overlayTextSecondary,
                        }}
                    >
                        {state.notice}
                    </p>
                )}
                {state.error !== null && (
                    <p className="mt-4 text-center text-xs text-red-500">
                        {state.error}
                    </p>
                )}
            </div>
            <div
                className="shrink-0 border-t p-[12px]"
                style={{
                    "borderColor":     palette.overlayBorder,
                    "backgroundColor": palette.modalCardBackground,
                }}
            >
                <div className="flex items-stretch justify-between gap-2.5">
                    <button
                        aria-label="Modifier"
                        className="flex shrink-0 items-center gap-1 rounded-xl border px-3 text-xs font-semibold transition-opacity hover:opacity-80"
                        onClick={goBack}
                        style={{
                            "backgroundColor": palette.interactiveHoverBackground,
                            "borderColor":     palette.overlayBorderStrong,
                            "color":           palette.modalCardTextPrimary,
                        }}
                        type="button"
                    >
                        <IonIcon
                            aria-hidden="true"
                            icon={chevronBackOutline}
                            style={{ "fontSize": "16px" }}
                        />
                        Modifier
                    </button>
                    <button
                        className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
                        disabled={lines.length === 0}
                        onClick={() => void confirm( state.checkoutMode )}
                        style={{
                            "backgroundColor": palette.brandPrimary,
                            "color":           palette.textOnBrandPrimary,
                        }}
                        type="button"
                    >
                        {state.checkoutMode === "direct" && (
                            <IonIcon
                                aria-hidden="true"
                                icon={flashOutline}
                                style={{ "fontSize": "16px" }}
                            />
                        )}
                        {state.checkoutMode === "direct" ? "Payer en un clic" : "Payer"}
                        {state.checkoutMode !== "direct" && (
                            <IonIcon
                                aria-hidden="true"
                                icon={chevronForwardOutline}
                                style={{ "fontSize": "16px" }}
                            />
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
